import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkle, Send, Users, MailCheck, Crown, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [key, setKey] = useState("");
    const [authed, setAuthed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [overview, setOverview] = useState(null);
    const [waveSize, setWaveSize] = useState(25);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const loadOverview = async (k) => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`${API}/admin/overview`, { headers: { "X-Admin-Key": k || key } });
            setOverview(data);
            setAuthed(true);
        } catch {
            setError("Invalid admin key.");
        }
        setLoading(false);
    };

    const sendWave = async () => {
        setSending(true);
        setResult(null);
        try {
            const { data } = await axios.post(
                `${API}/admin/beta-invites`,
                { limit: Number(waveSize) },
                { headers: { "X-Admin-Key": key } }
            );
            setResult(data);
            await loadOverview();
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to send wave.");
        }
        setSending(false);
    };

    return (
        <main data-testid="admin-page" className="min-h-screen bg-cream text-navy">
            <header className="border-b border-warmgray bg-cream/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
                    <Link to="/" data-testid="admin-logo" aria-label="Ago GenApp home">
                        <img src="/logo.png" alt="Ago GenApp" className="h-10 w-auto" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="admin-back-home"
                        className="flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <ArrowLeft size={15} /> Back Home
                    </Link>
                </div>
            </header>

            <div className="mx-auto max-w-4xl px-6 py-16">
                <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">Admin</p>
                <h1 className="font-display text-4xl tracking-tight md:text-5xl">Beta invitations.</h1>

                {!authed ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 max-w-md rounded-3xl border-2 border-warmgray bg-ivory p-8">
                        <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">Admin key</label>
                        <input
                            data-testid="admin-key-input"
                            type="password"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            placeholder="Enter your admin key"
                            className="mt-3 w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                        />
                        {error && <p data-testid="admin-error" className="mt-3 text-sm text-red-700">{error}</p>}
                        <button
                            data-testid="admin-login-button"
                            onClick={() => loadOverview()}
                            disabled={loading || !key}
                            className="btn-gradient mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {loading && <Loader2 size={15} className="animate-spin" />}
                            Open Dashboard
                        </button>
                    </motion.div>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
                        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
                            {[
                                { icon: Users, label: "Waitlist total", value: overview.waitlist_total, testId: "admin-stat-waitlist" },
                                { icon: Send, label: "Pending invite", value: overview.waitlist_pending, testId: "admin-stat-pending" },
                                { icon: MailCheck, label: "Beta invited", value: overview.beta_invited, testId: "admin-stat-invited" },
                                { icon: Crown, label: "Supporters", value: overview.supporters, testId: "admin-stat-supporters" },
                            ].map((s) => (
                                <div key={s.label} data-testid={s.testId} className="rounded-3xl border-2 border-warmgray bg-ivory p-6">
                                    <div className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C98A94]">
                                        <s.icon size={13} /> {s.label}
                                    </div>
                                    <p className="mt-3 font-display text-3xl">{s.value}</p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 rounded-3xl border-2 border-warmgray bg-ivory p-8">
                            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">Send the next wave</p>
                            <p className="mt-3 text-sm leading-relaxed text-navy">
                                Invitations go to the longest-waiting members first, in the order they joined. Each person is only invited once.
                            </p>
                            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                                <input
                                    data-testid="wave-size-input"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={waveSize}
                                    onChange={(e) => setWaveSize(e.target.value)}
                                    className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach sm:w-40"
                                />
                                <button
                                    data-testid="send-wave-button"
                                    onClick={sendWave}
                                    disabled={sending || overview.waitlist_pending === 0}
                                    className="btn-gradient flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                                >
                                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                    Send Invitations
                                </button>
                            </div>
                            {result && (
                                <div data-testid="wave-result" className="mt-6 rounded-2xl bg-[#FDFBF9] p-5 text-sm text-navy">
                                    <p className="font-semibold">Wave complete: {result.sent.length} sent{result.failed.length > 0 ? `, ${result.failed.length} undeliverable` : ""}.</p>
                                    {result.sent.length > 0 && <p className="mt-2 break-all font-mono text-xs text-[#2C3347]">{result.sent.join(", ")}</p>}
                                    {result.failed.length > 0 && <p className="mt-2 break-all font-mono text-xs text-red-700">Undeliverable: {result.failed.join(", ")}</p>}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
        </main>
    );
}
